/*
   Just enough shell tokenizing to colour the snippets on this page: comments, strings,
   variables, flags, the command word and <placeholders>. Not a parser; a token it does not
   recognise stays plain text, which is the safe failure for a snippet someone will copy.
   A library (shiki, prism) would bring a grammar engine for six token kinds.
*/

export type TokenKind =
  "plain" | "comment" | "string" | "variable" | "flag" | "command" | "placeholder"

export interface Token {
  kind: TokenKind
  text: string
}

const VARIABLE = /^\$(\{[^}]*\}|[A-Za-z_][A-Za-z0-9_]*)/
const PLACEHOLDER = /^<[^<>\n]+>/

/** A string's contents, with the variables inside a double-quoted one picked out. */
function stringTokens(text: string, quote: string): Token[] {
  if (quote === "'") return [{ kind: "string", text }]
  const out: Token[] = []
  let buf = ""
  for (let i = 0; i < text.length;) {
    const rest = text.slice(i)
    const variable = VARIABLE.exec(rest) ?? PLACEHOLDER.exec(rest)
    if (variable) {
      if (buf) out.push({ kind: "string", text: buf })
      buf = ""
      out.push({
        kind: variable[0].startsWith("$") ? "variable" : "placeholder",
        text: variable[0],
      })
      i += variable[0].length
    } else {
      buf += text[i]
      i += 1
    }
  }
  if (buf) out.push({ kind: "string", text: buf })
  return out
}

function lineTokens(line: string, continued: boolean): Token[] {
  const out: Token[] = []
  let atCommand = !continued
  let i = 0
  const push = (kind: TokenKind, text: string) => {
    const last = out[out.length - 1]
    if (kind === "plain" && last?.kind === "plain") last.text += text
    else out.push({ kind, text })
  }

  while (i < line.length) {
    const rest = line.slice(i)
    const ch = line[i] ?? ""
    const prev = i === 0 ? " " : (line[i - 1] ?? " ")

    if (ch === "#" && /\s/.test(prev)) {
      push("comment", rest)
      break
    }
    if (ch === "'" || ch === '"') {
      const end = line.indexOf(ch, i + 1)
      const stop = end === -1 ? line.length : end + 1
      for (const t of stringTokens(line.slice(i, stop), ch)) push(t.kind, t.text)
      i = stop
      atCommand = false
      continue
    }
    const variable = VARIABLE.exec(rest)
    if (variable) {
      push("variable", variable[0])
      i += variable[0].length
      atCommand = false
      continue
    }
    const placeholder = PLACEHOLDER.exec(rest)
    if (placeholder) {
      push("placeholder", placeholder[0])
      i += placeholder[0].length
      atCommand = false
      continue
    }
    if (/\s/.test(ch)) {
      push("plain", ch)
      i += 1
      continue
    }
    if (ch === "|" || ch === ";" || ch === "&") {
      push("plain", ch)
      i += 1
      atCommand = true
      continue
    }
    const word = /^[^\s'"|;&]+/.exec(rest)?.[0] ?? ch
    if (atCommand && !word.includes("=")) push("command", word)
    else if (word.startsWith("-") && /\s/.test(prev)) {
      // `-F name=value`: the flag is the part before a value glued to it.
      push("flag", word)
    } else push("plain", word)
    if (!word.includes("=")) atCommand = false
    i += word.length
  }
  return out
}

/** One token list per line, so the caller can render line by line. */
export function highlightShell(code: string): Token[][] {
  const lines = code.split("\n")
  let continued = false
  return lines.map((line) => {
    const tokens = lineTokens(line, continued)
    continued = /\\\s*$/.test(line)
    return tokens
  })
}

export const TOKEN_CLASS: Record<TokenKind, string> = {
  plain: "text-text",
  comment: "italic text-text-3",
  string: "text-master",
  variable: "text-people",
  flag: "text-info",
  command: "font-semibold text-brand",
  placeholder: "text-print",
}
